<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateFinancePayersRows from './RealEstateFinancePayersRows.vue';
import RealEstateFinancePayersSummary from './RealEstateFinancePayersSummary.vue';
import {
  formatPayerCents,
  payerFormFromInstallment,
  payerRowsPayload,
  resizePayerRows,
  totalPayerCents,
  validPayerRows,
} from './realEstateFinancePayersForm';

const props = defineProps({
  installment: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const count = ref(1);
const rows = ref([]);

const options = computed(() => props.installment?.compradores_vendedores || []);
const installmentCents = computed(
  () => props.installment?.parcela?.valor_parcela?.amount_cents || 0
);
const selectedCents = computed(() => totalPayerCents(rows.value));
const totalLabel = computed(() => formatPayerCents(selectedCents.value));
const installmentLabel = computed(() =>
  formatPayerCents(installmentCents.value)
);
const commissionLabel = computed(() =>
  formatPayerCents(
    props.installment?.comissao_geral?.valor_comissao_liquida?.amount_cents || 0
  )
);
const mismatch = computed(() => selectedCents.value !== installmentCents.value);
const valid = computed(() =>
  validPayerRows({
    rows: rows.value,
    optionsLength: options.value.length,
    installmentCents: installmentCents.value,
  })
);

const setCount = value => {
  const resized = resizePayerRows({
    rows: rows.value,
    count: value,
    maxCount: options.value.length,
    installmentCents: installmentCents.value,
  });
  count.value = resized.count;
  rows.value = resized.rows;
};

const updateRow = ({ index, field, value }) => {
  rows.value = rows.value.map((row, rowIndex) =>
    rowIndex === index ? { ...row, [field]: value } : row
  );
};

const reset = () => {
  const form = payerFormFromInstallment(props.installment);
  count.value = form.count;
  rows.value = form.rows;
};

const confirm = () => {
  if (!valid.value) return;
  emit('confirm', payerRowsPayload(rows.value));
};

watch(
  () => props.show,
  show => show && reset(),
  { immediate: true }
);
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('REAL_ESTATE_FINANCE.PAYERS_FORM.TITLE')"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <RealEstateFinancePayersSummary
        :commission-label="commissionLabel"
        :installment-label="installmentLabel"
        :total-label="totalLabel"
      />
      <RealEstateFinancePayersRows
        :count="count"
        :loading="loading"
        :options="options"
        :rows="rows"
        @change-count="setCount"
        @update-row="updateRow"
      />

      <p
        v-if="mismatch && rows.length"
        class="rounded-lais bg-red-50 p-3 text-sm text-red-700"
      >
        {{
          t('REAL_ESTATE_FINANCE.PAYERS_FORM.TOTAL_MISMATCH', {
            current: totalLabel,
            expected: installmentLabel,
          })
        }}
      </p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :disabled="!valid"
          :is-loading="loading"
          @click="confirm"
        >
          {{ t('REAL_ESTATE_FINANCE.PAYERS_FORM.SAVE') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
